import { Grid, useEventCallback } from '@mui/material';
import { memo, useMemo } from 'react';
import Table from 'shell/components/shared/table';
import {
  TableNewFamilySubjectsVM,
  TableNewFamilySubjectsBodyVM,
} from 'practice/view-models/practice-edit-residence';
import { headCellsNewFamilySubjectsBody } from '../constant';
import { noop } from 'shell/utils/noop';
import ParenthoodRelationshipList from 'shell/components/shared/parenthood-relationship-list';
import filterParenthood from 'shell/components/shared/parenthood-relationship-list/filter-parenthood';

interface TableNewFamilyProps {
  disabled?: boolean;
  tableNewFamilySubjects?: TableNewFamilySubjectsVM;
  onChangeTableNewFamily: (values: TableNewFamilySubjectsVM) => void;
  noControl?: boolean;
}

const TableNewFamilyComponent = ({
  disabled,
  tableNewFamilySubjects,
  noControl,
  onChangeTableNewFamily,
}: TableNewFamilyProps): React.ReactElement => {
  const onChange = useEventCallback(
    (
      key: keyof TableNewFamilySubjectsVM,
      value: TableNewFamilySubjectsVM[keyof TableNewFamilySubjectsVM]
    ) => {
      onChangeTableNewFamily({
        ...(tableNewFamilySubjects || {}),
        [key]: value,
      });
    }
  );

  const onChangePage = useEventCallback((page: number) => {
    onChange('page', page);
  });

  const onChangeRowsPerPage = useEventCallback((rowsPerPage: number) => {
    onChangeTableNewFamily({
      ...(tableNewFamilySubjects || {}),
      page: 0,
      rowsPerPage,
    });
  });

  const body: Array<TableNewFamilySubjectsBodyVM> = useMemo(() => {
    if (!tableNewFamilySubjects?.body?.length) {
      return [];
    }

    return tableNewFamilySubjects?.body?.map((item) => {
      const isHeadParenthood =
        item.hideRelationship || noControl
          ? false
          : !!tableNewFamilySubjects?.body?.find((item1) => {
              if (item?.posizionePersona === 'X') {
                return false;
              }
              if (item1.fiscalCode !== item.fiscalCode) {
                // non mostra intestatario se già presente dai soggetti con parentela disabilitata
                return item1.relationshipValue?.toString() === '1';
              }
              return false;
            });

      const isHeadMarriage =
        item.hideRelationship || noControl
          ? false
          : !!tableNewFamilySubjects?.body?.find((item1) => {
              if (item?.posizionePersona === 'X') {
                return false;
              }
              if (item1.fiscalCode !== item.fiscalCode) {
                // non marito/moglie se già presente dai soggetti con parentela disabilitata
                return item1.relationshipValue?.toString() === '2';
              }
              return false;
            });

      return {
        ...item,
        relationshipValue: isHeadParenthood || isHeadMarriage ? undefined : item.relationshipValue,
        relationship: (
          <ParenthoodRelationshipList
            isHeadParenthood={isHeadParenthood}
            isHeadMarriage={isHeadMarriage}
            isPractice
            filterParenthood={filterParenthood}
            disabled={item.hideRelationship || item.posizionePersona === 'X' || disabled}
            value={item.relationshipValue || ''}
            onChange={(value) => {
              const mappedElements = tableNewFamilySubjects?.body?.map((item1) =>
                item1.fiscalCode === item.fiscalCode
                  ? { ...item1, relationshipValue: value }
                  : item1
              );
              onChange('body', mappedElements);
            }}
          />
        ),
      };
    }) as Array<TableNewFamilySubjectsBodyVM>;
  }, [tableNewFamilySubjects?.body, onChange]);

  return (
    <Grid item xs={12}>
      <Table
        ariaLabel="Lista familiari"
        page={tableNewFamilySubjects?.page || 0}
        rowsPerPage={tableNewFamilySubjects?.rowsPerPage}
        body={body}
        count={body.length}
        head={headCellsNewFamilySubjectsBody}
        onPageChange={onChangePage}
        onRowsPerPageChange={onChangeRowsPerPage}
        orders={{}}
        isNotLayoutFixed
        noSort
        onSort={noop}
        hideButtons
      />
    </Grid>
  );
};

TableNewFamilyComponent.displayName = 'Accordion New Family Subjects component';

const TableNewFamily = memo(TableNewFamilyComponent);

export default TableNewFamily;
